/**
 * Capa de abstracción de autenticación: selecciona la implementación según
 * `VITE_AUTH_PROVIDER` ("entra" | "keycloak", por defecto "keycloak") y
 * re-exporta la API común que consume el resto de la app.
 *
 * Contratos estables (no cambiar firmas): `api/client.ts` y
 * `auth-provider.tsx` importan desde "@/lib/auth" sin saber qué proveedor
 * hay debajo.
 */

import { entraProvider } from "./entra";
import { keycloakProvider } from "./keycloak";
import type { AuthProvider } from "./types";

export { AuthError } from "./types";
export type { AuthUser, AuthProvider } from "./types";

function resolveProvider(): AuthProvider {
  const selected = (import.meta.env.VITE_AUTH_PROVIDER || "keycloak")
    .trim()
    .toLowerCase();
  return selected === "entra" ? entraProvider : keycloakProvider;
}

/** Proveedor activo, resuelto una vez al cargar el módulo. */
export const auth: AuthProvider = resolveProvider();

/** true si el proveedor activo es Entra (login interactivo de Microsoft). */
export const isEntra = auth.kind === "entra";
/** true si el proveedor activo usa formulario embebido (keycloak). */
export const usesCredentialsForm = auth.usesCredentialsForm;

// --- API común re-exportada (mismas firmas que antes) ------------------------

export const initialize = (): Promise<void> => auth.initialize();

export const login = (username: string, password: string) =>
  auth.login(username, password);

export const loginInteractive = (): Promise<void> => auth.loginInteractive();

export const logout = (): Promise<void> => auth.logout();

export const refreshIfNeeded = (): Promise<string | null> =>
  auth.refreshIfNeeded();

export const getAccessToken = (): string | null => auth.getAccessToken();

export const getCurrentUser = () => auth.getCurrentUser();

export const restoreSession = () => auth.restoreSession();
